import React, { useContext, useRef, useState, useEffect } from 'react';
import { css } from 'emotion';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { useTransition, animated } from 'react-spring';

import FontAwesome from '@vromo/ui/components/FontAwesome';
import theme from '@vromo/ui/components/theme';
import last from 'lodash/last';
import getTestIdWithModifier from '../helpers';
import { qaLabelCommon } from './../../../common-qa-labels/common';

const toastProperties = {
    error: { icon: 'exclamation-circle', color: theme.danger },
    warning: { icon: 'exclamation-triangle', color: theme.primary },
    info: { icon: 'info-circle', color: '#1590CE' },
    success: { icon: 'check-circle', color: '#43C96E' }
};

type ToastType = 'error' | 'warning' | 'info' | 'success';

class Toast {
    message: string;
    type: ToastType = 'info';
    timeout: number | null = 5000;
    icon: string;
    color: string;
    testId?: string;

    constructor(
        message: string,
        type?: ToastType,
        timeout?: number | null,
        icon?: string,
        testId?: string
    ) {
        this.message = message;
        if (type) this.type = type;
        if (timeout !== undefined) this.timeout = timeout;
        icon ? (this.icon = icon) : (this.icon = toastProperties[this.type].icon);
        this.color = toastProperties[this.type].color;
        this.testId = testId;
    }
}

const ToastContext = React.createContext<any>([]);
let toastId = 0;

export function useToasts() {
    const [lastMessage, setToast] = useContext(ToastContext);

    function showToast(
        message: string,
        type?: ToastType,
        timeout?: number | null,
        icon?: string,
        testId?: string
    ) {
        const toast = new Toast(message, type, timeout, icon, testId);

        if (!(lastMessage == message)) {
            setToast((state: Array<{ key: number; toast: Toast }>) => [
                ...state,
                { key: toastId++, toast: toast }
            ]);
        }
    }

    return showToast;
}

export default observer(function Toasts({ children }) {
    const [stateToasts, setStateToasts] = useState<Array<{ key: number; toast: Toast }>>([]);
    let lastMessage;

    useEffect(() => {
        stateToasts.length > 0 ? (lastMessage = last(stateToasts)?.toast.message) : null;
    }, [stateToasts.length]);

    const config = { tension: 125, friction: 20, precision: 0.1 };

    const [refMap] = useState(() => new WeakMap());
    const { current: cancelMap } = useRef(new WeakMap<any, Function>());

    const transitions = useTransition(stateToasts, (item) => item.key, {
        from: ({ key }) => ({
            opacity: 0,
            height: 0,
            life: 0
        }),
        enter: (item) => async (next) =>
            await next({ opacity: 1, height: refMap.get(item).offsetHeight }),
        leave: (item) => async (next, cancel) => {
            cancelMap.set(item, cancel);
            await next({ life: 0 });
            await next({ opacity: 0 });
            await next({ height: 0 }, true);
        },
        onRest: (item) => setStateToasts((items) => items.filter((i) => i.key !== item.key)),
        config: ({ key, toast }, state) =>
            state === 'leave' ? [{ duration: toast.timeout }, config, config] : config
    });

    return (
        <ToastContext.Provider value={[lastMessage, setStateToasts]}>
            <>
                <ToastContainer>
                    {transitions.map(({ key, item, props: { ...style } }) => {
                        const { toast } = item;
                        return (
                            <ToastMessage
                                data-testid={getTestIdWithModifier('toast-message', toast.testId)}
                                key={key}
                                color={toast.color}
                                style={{
                                    ...style
                                }}>
                                <div
                                    className={css`
                                        display: flex;
                                        align-items: center;
                                        justify-content: space-between;
                                        padding: 10px;
                                    `}
                                    ref={(ref) => ref && refMap.set(item, ref)}>
                                    <FontAwesome
                                        icon={toast.icon}
                                        style={{ marginRight: 10 }}
                                        weight="duotone"
                                        x2
                                    />
                                    <div data-testid={qaLabelCommon.toastText} className={css`
                                        font-weight: 500;
                                        font-size: 14px;
                                        :first-letter {text-transform: uppercase}
                                    `}>
                                        {toast.message}
                                    </div>
                                    <CloseToast
                                        icon="times"
                                        key={key}
                                        onClick={() =>
                                            cancelMap.has(item) && cancelMap.get(item)!()
                                        }
                                    />
                                </div>
                            </ToastMessage>
                        );
                    })}
                </ToastContainer>
                {children}
            </>
        </ToastContext.Provider>
    );
});

const ToastContainer = styled.div`
    max-width: 400px;
    z-index: 50000;
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
`;

const ToastMessage = styled(animated.div)`
    position: relative;
    margin-top: 5px;
    border-radius: 4px;
    background-color: ${({ color }) => color};
    color: #fff;
    overflow: hidden;
`;

const CloseToast = styled(FontAwesome)`
    transition: all 150ms ease-in-out;
    opacity: 0.5;
    margin-left: 15px;
    cursor: pointer;

    &:hover {
        opacity: 1;
    }
`;
